import { motion } from "framer-motion";
import { Reveal, SectionTag, CountUp } from "@/components/primitives";
import { useLanguage } from "@/hooks/use-language";

const BIG_STATS = [
  { to: 90.2, decimals: 1, suffix: "%" },
  { to: 0.05, decimals: 2, prefix: "$" },
  { to: 42, decimals: 0, suffix: "k" },
];

const BARS = [
  { ours: 90.2, theirs: 17.7, oursText: "90.2%", theirsText: "17.7%" },
  { ours: 3, theirs: 100, oursText: "~$0.05", theirsText: "~$1.80" },
  { ours: 8, theirs: 100, oursText: "42k", theirsText: "546k" },
];

export const Benchmark = () => {
  const { t } = useLanguage();
  return (
  <section id="benchmark" className="py-24 lg:py-32 border-b border-border" data-testid="benchmark-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <SectionTag index="05" label={t.bench.tag} />
        <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl max-w-3xl">
          {t.bench.h2}
        </h2>
        <p className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground">
          {t.bench.sub}
        </p>
      </Reveal>

      <div className="mt-16 grid md:grid-cols-3 border border-border">
        {BIG_STATS.map((s, i) => (
          <Reveal key={i} delay={i * 0.1} className={`${i > 0 ? "border-t md:border-t-0 md:border-l" : ""} border-border`}>
            <div className="p-10" data-testid={`benchmark-stat-${i}`}>
              <div className="font-display font-black text-5xl lg:text-6xl text-gradient tracking-tight">
                <CountUp to={s.to} decimals={s.decimals} prefix={s.prefix || ""} suffix={s.suffix || ""} />
              </div>
              <div className="mt-4 font-display font-bold text-sm uppercase tracking-[0.2em]">{t.bench.stats[i].label}</div>
              <div className="mt-2 text-sm text-muted-foreground">{t.bench.stats[i].sub}</div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-6 border border-border p-8 lg:p-12" data-testid="benchmark-bars">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-muted-foreground mb-10">
            <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-gradient-to-r from-teal-500 to-green-500" /> Streamline360</span>
            <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-muted-foreground/30" /> {t.bench.theirs}</span>
          </div>
          <div className="space-y-10">
            {BARS.map((bar, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm font-medium mb-3">
                  <span>{t.bench.bars[i]}</span>
                  <span className="text-muted-foreground">{bar.oursText} <span className="mx-1 opacity-40">/</span> {bar.theirsText}</span>
                </div>
                <div className="space-y-2">
                  <div className="h-2.5 w-full bg-secondary overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${bar.ours}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full bg-gradient-to-r from-teal-500 to-green-500"
                    />
                  </div>
                  <div className="h-2.5 w-full bg-secondary overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${bar.theirs}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full bg-muted-foreground/30"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-xs text-muted-foreground">
            {t.bench.footnote}
          </p>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
